/**
 * Pure color / shading resolution for flowchart nodes.
 *
 * This module owns the two visually-load-bearing decisions the flowchart node
 * makes, kept out of the component so they are testable and side-effect-free:
 *
 *  1. **Custom-color-as-inner-shading** — a `classDef`/inline `fill:`/`stroke:`
 *     must NEVER be applied verbatim (a light `fill:#e1f5fe` over light text is
 *     unreadable). Instead the node keeps the dark {@link NODE_FILL} base and a
 *     readable {@link NODE_TEXT} label, and the author's color shows only as a
 *     subtle inner wash + the silhouette accent. {@link resolveFlowchartNodeColors}
 *     produces the blended fill, the legible-on-dark accent, and the text color.
 *
 *  2. **The mindmap's box-shadow system, accent-tinted** — the same inset
 *     accent ring + soft drop shadow + white top highlight + shape-following
 *     selection glow the mindmap uses, but tinted with the flowchart node's own
 *     per-shape accent instead of the mindmap's fixed blue depth tiers.
 *     {@link buildFlowchartBoxShadow} composes it.
 *
 * Colors are blended with CSS `color-mix()` so any valid CSS color the parser
 * captured (hex, `rgb()`, named) is handled uniformly — no brittle hex parsing.
 */

import { NODE_FILL, NODE_TEXT } from '../Cmarkdowngraphcontrols';
import { NODE_HAIRLINE } from './flowchart-constants';
import { FlowchartLabelOrigin, FlowchartNodeRole } from './parse-mermaid-flowchart';
import type { FlowchartNodeStyle } from './parse-mermaid-flowchart';

/** Accent used for an unresolved (dangling-reference) node's diagnostic chrome. */
const UNRESOLVED_ACCENT = '#64748B';

/**
 * Legible neutral for a shape icon on a plain (hairline-outlined) card. A glyph
 * stroked in the faint {@link NODE_HAIRLINE} outline color would all but vanish,
 * so a neutral process/data card draws its icon in this readable muted tone while
 * its silhouette keeps the calm hairline. Accented shapes keep the accent glyph.
 */
const NEUTRAL_ICON_COLOR = 'rgba(255, 255, 255, 0.55)';

/**
 * Resolve the color for a shape's icon glyph: the accent when it is a real
 * (visible) color, but a legible neutral when the accent is the faint card
 * hairline, so a plain process/data card's icon stays readable.
 */
export function resolveFlowchartIconColor(accent: string): string {
  return accent === NODE_HAIRLINE ? NEUTRAL_ICON_COLOR : accent;
}

/** Label color for an unresolved node (muted, still readable on the dark fill). */
const UNRESOLVED_TEXT = 'rgba(148, 163, 184, 0.92)';

/**
 * Dim opacity floor for off-path nodes. Raised well above the mindmap's 0.25
 * so a dimmed flowchart node stays readable (issue #3) while still receding.
 */
const FLOWCHART_DIMMED_OPACITY = 0.42;

/**
 * How much of the author's custom fill bleeds into the dark base. Kept low so
 * the base stays dark and the label stays readable; the accent + ring carry the
 * rest of the author's intent. A single flat mix (not a gradient) so the same
 * value is a valid paint for every silhouette — CSS background, clip-path layer,
 * and SVG `fill` alike.
 */
const CUSTOM_FILL_WASH = '16%';
/** Percent of white mixed into a custom color so it reads as an accent on dark. */
const ACCENT_WHITE_MIX_PERCENT = 32;

/**
 * Resting/emphasized strengths for the accent-tinted box shadow. The accent is
 * now the literal border color the look specifies (a faint neutral hairline for a
 * plain process, a saturated green/blue/red for the accented shapes), so the ring
 * renders it at near-full strength rather than re-diluting it. That keeps a
 * neutral process a true hairline and an accented node's outline crisp.
 */
const RING_ALPHA_RESTING = '92%';
const RING_ALPHA_EMPHASIZED = '100%';
const HIGHLIGHT_ALPHA = 'rgba(255, 255, 255, 0.22)';
const DROP_SHADOW = 'rgba(0, 0, 0, 0.55) 1px 2px 6px';

/** Strength of the shape-following selection glow. */
const SELECTION_GLOW_SPREAD = '0 0 26px -4px';
const SELECTION_GLOW_ALPHA = '62%';

/**
 * Soft resting outer halo for the Start terminator: its always-on "origin" glow
 * (the green presence the old radial fill implied). Dimmer/tighter than the
 * selection glow so it reads as ambient, not active.
 */
const RESTING_GLOW_SPREAD = '0 0 18px -6px';
const RESTING_GLOW_ALPHA = '45%';

/**
 * The Start terminator reads as a transparent-dark pill with a green outline,
 * a soft green outer glow, and a green label: the diagram's single "go" color.
 */
const START_RING_ACCENT = '#66FEC1';
/** Start terminator label color: the same green as its outline/glow. */
const START_TEXT = '#66FEC1';

/**
 * The End terminator reads as a near-black maroon pill: a tinted dark-red fill
 * (never pure red), a muted red outline, and a plain white label.
 */
const END_FILL = '#2A1418';
const END_BORDER = 'rgba(248, 113, 113, 0.5)';

/** Resolved role-driven chrome for a terminator (Start/End) or a non-terminator. */
export interface FlowchartRoleColors {
  /** Silhouette / outline / glow accent (already legible on the dark base). */
  accent: string;
  /** Interior fill. */
  fill: string;
  /** Label color. */
  textColor: string;
}

/**
 * Resolve a node's role-driven chrome, layering the terminator identity over the
 * shape-resolved colors:
 *
 *  - {@link FlowchartNodeRole.Start}: dark fill, green outline + glow, green label.
 *  - {@link FlowchartNodeRole.End}: maroon fill, muted-red outline, white label.
 *  - {@link FlowchartNodeRole.None}: passes the shape-resolved colors through.
 *
 * Role identity wins for terminators so a Start/End never inherits an author's
 * custom wash; every other node keeps its resolved (calm) treatment.
 */
export function resolveFlowchartRoleColors(input: {
  role: FlowchartNodeRole;
  resolved: FlowchartNodeColors;
}): FlowchartRoleColors {
  const byRole: Record<FlowchartNodeRole, FlowchartRoleColors> = {
    [FlowchartNodeRole.Start]: {
      accent: START_RING_ACCENT,
      fill: NODE_FILL,
      textColor: START_TEXT,
    },
    [FlowchartNodeRole.End]: {
      accent: END_BORDER,
      fill: END_FILL,
      textColor: NODE_TEXT,
    },
    [FlowchartNodeRole.None]: {
      accent: input.resolved.accent,
      fill: input.resolved.fill,
      textColor: input.resolved.textColor,
    },
  };
  return byRole[input.role];
}

/** Fully-resolved colors for a flowchart node, ready to hand to the frame. */
export interface FlowchartNodeColors {
  /** Silhouette / border / icon accent — always legible against the dark fill. */
  accent: string;
  /** Interior fill — dark base, optionally washed with the author's custom color. */
  fill: string;
  /** Label color — always a readable on-dark value (never the verbatim custom color). */
  textColor: string;
}

/**
 * Make an arbitrary author color legible as an accent on the dark base.
 * Light pastels (e.g. `#e1f5fe`) already read; darker strokes (e.g. `#01579b`)
 * get lifted toward white so the silhouette/icon stays visible.
 */
function toLegibleAccent(color: string): string {
  return `color-mix(in srgb, ${color} ${100 - ACCENT_WHITE_MIX_PERCENT}%, #ffffff ${ACCENT_WHITE_MIX_PERCENT}%)`;
}

/**
 * The dark base fill, washed with a low-opacity veil of the author's custom
 * color so their intent reads as an inner tint without lifting the base (which
 * would wreck label contrast). A single flat color so it is a valid paint for
 * every silhouette (CSS background, clip-path layer, and SVG `fill`).
 */
function toWashedFill(customColor: string): string {
  return `color-mix(in srgb, ${customColor} ${CUSTOM_FILL_WASH}, ${NODE_FILL})`;
}

/** Inputs to {@link resolveFlowchartNodeColors}. */
export interface ResolveFlowchartNodeColorsInput {
  resolvedStyle: FlowchartNodeStyle | null;
  labelOrigin: FlowchartLabelOrigin;
  /** The accent the layout already resolved (`stroke ?? fill ?? shape default`). */
  accentColor: string;
}

interface CustomNodeColors {
  fill: string | null;
  stroke: string | null;
}

function getCustomNodeColors(style: FlowchartNodeStyle | null): CustomNodeColors {
  return {
    fill: style?.fill ?? null,
    stroke: style?.stroke ?? null,
  };
}

function resolveCustomNodeAccent(input: {
  customColors: CustomNodeColors;
  fallbackAccent: string;
}): string {
  if (input.customColors.stroke) {
    return toLegibleAccent(input.customColors.stroke);
  }

  if (input.customColors.fill) {
    return toLegibleAccent(input.customColors.fill);
  }

  return input.fallbackAccent;
}

function resolveCustomNodeFill(customFill: string | null): string {
  return customFill ? toWashedFill(customFill) : NODE_FILL;
}

/**
 * Resolve the node's accent / fill / text, expressing any author custom color
 * as inner shading + accent rather than a verbatim fill (issue #2).
 */
export function resolveFlowchartNodeColors(
  input: ResolveFlowchartNodeColorsInput,
): FlowchartNodeColors {
  if (input.labelOrigin === FlowchartLabelOrigin.Unresolved) {
    return {
      accent: UNRESOLVED_ACCENT,
      fill: NODE_FILL,
      textColor: UNRESOLVED_TEXT,
    };
  }

  const customColors = getCustomNodeColors(input.resolvedStyle);

  return {
    // Prefer the author's stroke as the accent (lifted for legibility), else
    // their fill, else the per-shape default the layout already chose.
    accent: resolveCustomNodeAccent({
      customColors,
      fallbackAccent: input.accentColor,
    }),
    // The custom *fill* becomes a wash over the dark base; a custom *stroke*
    // alone (no fill) is expressed purely through the accent.
    fill: resolveCustomNodeFill(customColors.fill),
    // Label stays on the readable on-dark value — never the author's `color:`,
    // which assumes their (light) background and would be unreadable here.
    textColor: NODE_TEXT,
  };
}

/** Inputs to {@link buildFlowchartBoxShadow}. */
export interface BuildFlowchartBoxShadowInput {
  /** The node's resolved accent (already legible-on-dark): outline ring + glow color. */
  accent: string;
  /** Emphasized = hovered/focused node: wider ring, no growth. */
  isEmphasized: boolean;
  /** Selected = the shape-following selection glow is appended. */
  isSelected: boolean;
  /** Whether this silhouette can carry inset rings (BorderRadius can; clip/svg cannot). */
  supportsInsetRings: boolean;
  /**
   * Whether to add a soft always-on outer halo (the Start terminator's "origin"
   * glow). Only honored for inset-ring silhouettes (Start/End are both pills).
   */
  restingGlow: boolean;
}

function buildSelectionGlow(input: {
  isSelected: boolean;
  ringColor: string;
}): string {
  if (!input.isSelected) {
    return '';
  }

  return `${SELECTION_GLOW_SPREAD} color-mix(in srgb, ${input.ringColor} ${SELECTION_GLOW_ALPHA}, transparent)`;
}

function buildRestingGlow(input: {
  isSelected: boolean;
  restingGlow: boolean;
  ringColor: string;
}): string {
  if (!input.restingGlow || input.isSelected) {
    return '';
  }

  return `${RESTING_GLOW_SPREAD} color-mix(in srgb, ${input.ringColor} ${RESTING_GLOW_ALPHA}, transparent)`;
}

function getOuterGlow(input: {
  isSelected: boolean;
  restingGlow: boolean;
  ringColor: string;
  selectionGlow: string;
}): string {
  return input.selectionGlow || buildRestingGlow({
    isSelected: input.isSelected,
    restingGlow: input.restingGlow,
    ringColor: input.ringColor,
  });
}

/**
 * Compose the accent-tinted box shadow: an inset accent ring + soft drop shadow
 * + a faint white top highlight, plus the selection glow when selected. Mirrors
 * the mindmap's depth-shadow structure, tinted with the node's own accent.
 *
 * For silhouettes that cannot carry inset rings (clip-path / svg bodies), only
 * the outer glow is returned (an inset ring would not follow the clip) — the
 * frame draws the accent border on the silhouette itself in those cases.
 */
export function buildFlowchartBoxShadow(input: BuildFlowchartBoxShadowInput): string {
  const ringColor = input.accent;
  const selectionGlow = buildSelectionGlow({
    isSelected: input.isSelected,
    ringColor,
  });

  if (!input.supportsInsetRings) {
    return selectionGlow;
  }

  const ringAlpha = input.isEmphasized ? RING_ALPHA_EMPHASIZED : RING_ALPHA_RESTING;
  const ringWidth = input.isEmphasized ? '1.75px' : '1.5px';
  const ring = `inset 0 0 0 ${ringWidth} color-mix(in srgb, ${ringColor} ${ringAlpha}, transparent)`;
  const highlight = `inset ${HIGHLIGHT_ALPHA} 1px 2px 5px`;

  const layers = [ring, DROP_SHADOW, highlight];
  const outerGlow = getOuterGlow({
    isSelected: input.isSelected,
    restingGlow: input.restingGlow,
    ringColor,
    selectionGlow,
  });
  return outerGlow ? `${layers.join(', ')}, ${outerGlow}` : layers.join(', ');
}

/** Off-path nodes recede to a readable floor rather than near-invisible. */
export function getFlowchartNodeOpacity(input: { isActive: boolean }): number {
  return input.isActive ? 1 : FLOWCHART_DIMMED_OPACITY;
}
